import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useWA, useLoad, useDebounced, useSelection, PageHead, Card, Tabs, Search, Btn, Badge, BulkBar, Check, Pager, Skeleton, Empty, ErrorBox, Avatar, num, ago, fdt, exportCsv } from "../ui";
import { siteLink } from "../api";

const TAKE = 40;

function CommentsTab({ blogId }) {
  const { run, confirm, prompt } = useWA();
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const dq = useDebounced(q);
  useEffect(() => setSkip(0), [dq]);
  const { data, loading, error, reload } = useLoad("comments", { q: dq, blogId, take: TAKE, skip }, [dq, blogId, skip]);
  const items = (data && data.items) || [];
  const S = useSelection(items);

  const act = async (action, params, msg, ask) => {
    if (ask && !(await confirm(ask))) return;
    try {
      await run(action, params, msg);
      reload();
    } catch (e) {
      /* toast */
    }
  };
  const edit = async (c) => {
    const text = await prompt({ title: "Comment edit करें", defaultValue: c.text, multiline: true });
    if (text === null || text === c.text) return;
    act("commentEdit", { id: c.id, text }, "Comment बदल गया");
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Search value={q} onChange={setQ} placeholder="Comment, नाम या blog खोजें…" className="flex-1 min-w-[220px]" />
        <Btn icon="fa-solid fa-download" disabled={!items.length} onClick={() => exportCsv(items.map((c) => ({ comment: c.text, by: c.by, blog: c.post, date: c.at })), "ycnp-comments")}>CSV</Btn>
      </div>
      {blogId && <p className="text-sm mb-3"><Badge tone="cyan">एक blog के comments</Badge> <Link to="/website/comments" className="underline text-xs ml-2">सब दिखाएँ</Link></p>}
      <BulkBar count={S.sel.length} onClear={S.clear}>
        <Btn size="sm" variant="danger" icon="fa-regular fa-trash-can" onClick={() => act("commentDelete", { ids: S.sel }, "Comments delete हो गए", { title: `${S.sel.length} comments delete करें?`, danger: true })}>Delete</Btn>
      </BulkBar>
      <ErrorBox msg={error} retry={reload} />
      <Card pad={false}>
        {loading && !data ? <div className="p-4"><Skeleton rows={6} h={60} /></div> : !items.length ? <Empty icon="fa-regular fa-comments" title="कोई comment नहीं" /> : (
          <>
            <div className="flex items-center gap-3 px-4 py-2.5 border-b wa-line text-[12px] wa-muted"><Check checked={S.all} onChange={S.toggleAll} label="Select all" /> सब चुनें · कुल {num(data.total)}</div>
            <ul>
              {items.map((c) => (
                <li key={c.id} className={"flex gap-3 px-4 py-3.5 border-b wa-line last:border-0 " + (S.has(c.id) ? "wa-sel" : "")} style={S.has(c.id) ? { background: "color-mix(in srgb, var(--wa-accent) 8%, transparent)" } : {}}>
                  <div className="pt-1"><Check checked={S.has(c.id)} onChange={() => S.toggle(c.id)} /></div>
                  <Avatar name={c.by} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px]">
                      <Link to={`/website/users?open=${c.userId}`} className="font-bold hover:underline">{c.by}</Link>
                      {c.userBlocked && <Badge tone="red" className="ml-1.5">Blocked</Badge>}
                      <span className="wa-muted"> · <a href={siteLink(`/blogs/${encodeURIComponent(c.slug)}`)} target="_blank" rel="noreferrer" className="hover:underline">{c.post}</a> · <span title={fdt(c.at)}>{ago(c.at)}</span></span>
                    </p>
                    <p className="text-[14px] mt-1 whitespace-pre-wrap break-words">{c.text}</p>
                  </div>
                  <div className="flex items-start gap-1 shrink-0">
                    <Btn size="sm" icon="fa-regular fa-pen-to-square" onClick={() => edit(c)} aria-label="Edit" title="Edit" />
                    {!c.userBlocked && <Btn size="sm" icon="fa-solid fa-user-slash" onClick={() => act("userBlock", { id: c.userId, blocked: true }, "User block हो गया", { title: `${c.by} को block करें?`, text: "वह login नहीं कर पाएगा और comment/like नहीं कर पाएगा।", danger: true, ok: "Block करें" })} aria-label="Block user" title="User block करें" />}
                    <Btn size="sm" className="!text-rose-600" icon="fa-regular fa-trash-can" onClick={() => act("commentDelete", { id: c.id }, "Comment delete हो गया", { title: "यह comment delete करें?", danger: true })} aria-label="Delete" title="Delete" />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        <Pager total={data && data.total} take={TAKE} skip={skip} onChange={setSkip} />
      </Card>
    </>
  );
}

function LikesTab({ initialKind }) {
  const { run, confirm } = useWA();
  const [kind, setKind] = useState(initialKind || "blog");
  const [skip, setSkip] = useState(0);
  useEffect(() => setSkip(0), [kind]);
  const { data, loading, error, reload } = useLoad("likes", { kind, take: TAKE, skip }, [kind, skip]);
  const items = (data && data.items) || [];
  const S = useSelection(items);
  const del = async () => {
    if (!(await confirm({ title: `${S.sel.length} likes हटाएँ?`, text: "Fake या spam likes हटाने के लिए।", danger: true }))) return;
    try {
      await run("likeDelete", { ids: S.sel, kind }, "Likes हट गए");
      reload();
    } catch (e) {
      /* toast */
    }
  };
  return (
    <>
      <div className="flex gap-2 mb-4"><Tabs tabs={[["blog", "Blog likes"], ["poster", "Poster likes"]]} value={kind} onChange={setKind} /></div>
      <BulkBar count={S.sel.length} onClear={S.clear}><Btn size="sm" variant="danger" icon="fa-regular fa-trash-can" onClick={del}>Like हटाएँ</Btn></BulkBar>
      <ErrorBox msg={error} retry={reload} />
      <Card pad={false}>
        {loading && !data ? <div className="p-4"><Skeleton rows={6} /></div> : !items.length ? <Empty icon="fa-regular fa-heart" title="कोई like नहीं" /> : (
          <div className="overflow-x-auto">
            <table className="wa-tbl">
              <thead><tr><th style={{ width: 36 }}><Check checked={S.all} onChange={S.toggleAll} /></th><th>किसने</th><th>{kind === "poster" ? "Poster" : "Blog"}</th><th>कब</th></tr></thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className={S.has(l.id) ? "wa-sel" : ""}>
                    <td><Check checked={S.has(l.id)} onChange={() => S.toggle(l.id)} /></td>
                    <td><Link to={`/website/users?open=${l.userId}`} className="font-semibold hover:underline">{l.by}</Link></td>
                    <td><a href={siteLink(`/${kind === "poster" ? "posters" : "blogs"}/${encodeURIComponent(l.slug)}`)} target="_blank" rel="noreferrer" className="hover:underline">{l.post}</a></td>
                    <td className="wa-muted text-[12.5px]">{fdt(l.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={items.length === TAKE ? skip + TAKE + 1 : skip + items.length} take={TAKE} skip={skip} onChange={setSkip} />
      </Card>
    </>
  );
}

export default function Comments() {
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const [tab, setTab] = useState(qs.get("tab") === "likes" ? "likes" : "comments");
  return (
    <div>
      <PageHead icon="fa-regular fa-comments" title="Comments & Likes" subtitle="गलत comments हटाएँ, edit करें, spam करने वालों को block करें" actions={<Tabs tabs={[["comments", "Comments"], ["likes", "Likes"]]} value={tab} onChange={setTab} />} />
      {tab === "comments" ? <CommentsTab blogId={qs.get("blog") || ""} /> : <LikesTab initialKind={qs.get("kind")} />}
    </div>
  );
}
